import * as THREE from 'three'
import type { BnBNode } from '@/game/bnb-manager'
import { computeAABBFromInequalities, generateVerticesFromConstraints } from '@/graphics/geometry'

export interface BnBGhostPlaneSpec {
  varIndex: number
  value: number
  aabb: ReturnType<typeof computeAABBFromInequalities>
}

export interface BnBGeometryPayload {
  children: BnBNode[]
  ghostPlanes: BnBGhostPlaneSpec[]
  childDegeneracy: Array<{ nodeId: string; vertices: THREE.Vector3[] }>
  currentDegeneracy: THREE.Vector3[]
}

const MAX_CACHE_ENTRIES = 180
const MAX_REVISIONS_PER_NODE = 4

type CacheEntry = {
  key: string
  nodeId: string
  payload: BnBGeometryPayload
  lastAccess: number
}

const bnbGeometryPayloadCache = new Map<string, CacheEntry>()
const nodeCacheKeys = new Map<string, Set<string>>()
let accessCounter = 0

const makeCacheKey = (nodeId: string, revision: string | number): string => `${nodeId}::${String(revision)}`

const touch = (entry: CacheEntry): void => {
  accessCounter += 1
  entry.lastAccess = accessCounter
}

const removeCacheKey = (key: string): void => {
  const entry = bnbGeometryPayloadCache.get(key)
  if (!entry) return

  bnbGeometryPayloadCache.delete(key)
  const nodeKeys = nodeCacheKeys.get(entry.nodeId)
  if (!nodeKeys) return

  nodeKeys.delete(key)
  if (nodeKeys.size === 0) {
    nodeCacheKeys.delete(entry.nodeId)
  }
}

const enforceNodeRevisionLimit = (nodeId: string): void => {
  const nodeKeys = nodeCacheKeys.get(nodeId)
  if (!nodeKeys || nodeKeys.size <= MAX_REVISIONS_PER_NODE) return

  while (nodeKeys.size > MAX_REVISIONS_PER_NODE) {
    let oldestKey: string | null = null
    let oldestAccess = Number.POSITIVE_INFINITY

    for (const key of nodeKeys) {
      const entry = bnbGeometryPayloadCache.get(key)
      if (!entry) continue
      if (entry.lastAccess < oldestAccess) {
        oldestAccess = entry.lastAccess
        oldestKey = key
      }
    }

    if (!oldestKey) break
    removeCacheKey(oldestKey)
  }
}

const enforceGlobalLimit = (): void => {
  while (bnbGeometryPayloadCache.size > MAX_CACHE_ENTRIES) {
    let oldestKey: string | null = null
    let oldestAccess = Number.POSITIVE_INFINITY

    for (const entry of bnbGeometryPayloadCache.values()) {
      if (entry.lastAccess < oldestAccess) {
        oldestAccess = entry.lastAccess
        oldestKey = entry.key
      }
    }

    if (!oldestKey) break
    removeCacheKey(oldestKey)
  }
}

export function computeBnBGeometryPayload(node: BnBNode, stateNodes: Map<string, BnBNode> | null | undefined): BnBGeometryPayload {
  const childIds = node.children || []
  const children = childIds
    .map((id) => stateNodes?.get(id))
    .filter((child): child is BnBNode => {
      if (!child) return false
      if (child.lp?.infeasible) return false
      if (child.status === 'pruned' && child.pruneReason === 'infeasible') return false
      return true
    })

  const baseProblem = node.problem
  const aabb = computeAABBFromInequalities(baseProblem.inequalities)

  const ghostPlanes: BnBGhostPlaneSpec[] = []
  children.forEach((child) => {
    if (!child.bounds || child.bounds.length === 0) return
    const lastBound = child.bounds[child.bounds.length - 1]
    ghostPlanes.push({
      varIndex: lastBound.varIndex,
      value: lastBound.value,
      aabb,
    })
  })

  const childDegeneracy = children.map((child) => ({
    nodeId: child.id,
    vertices: generateVerticesFromConstraints(child.problem.inequalities),
  }))

  const currentDegeneracy = children.length > 0 ? [] : generateVerticesFromConstraints(baseProblem.inequalities)

  return {
    children,
    ghostPlanes,
    childDegeneracy,
    currentDegeneracy,
  }
}

export function computeBnBGeometryPayloadCached(
  nodeId: string,
  node: BnBNode,
  stateNodes: Map<string, BnBNode> | null | undefined,
  revision: string | number,
): BnBGeometryPayload {
  const key = makeCacheKey(nodeId, revision)
  const cached = bnbGeometryPayloadCache.get(key)
  if (cached) {
    touch(cached)
    return cached.payload
  }

  const payload = computeBnBGeometryPayload(node, stateNodes)
  const entry: CacheEntry = {
    key,
    nodeId,
    payload,
    lastAccess: 0,
  }
  touch(entry)
  bnbGeometryPayloadCache.set(key, entry)

  const nodeKeys = nodeCacheKeys.get(nodeId) ?? new Set<string>()
  nodeKeys.add(key)
  nodeCacheKeys.set(nodeId, nodeKeys)

  enforceNodeRevisionLimit(nodeId)
  enforceGlobalLimit()
  return payload
}

export function clearBnBGeometryCache(nodeId?: string): void {
  if (!nodeId) {
    bnbGeometryPayloadCache.clear()
    nodeCacheKeys.clear()
    return
  }

  const nodeKeys = nodeCacheKeys.get(nodeId)
  if (!nodeKeys) return

  for (const key of nodeKeys) {
    bnbGeometryPayloadCache.delete(key)
  }
  nodeCacheKeys.delete(nodeId)
}

export function getBnBGeometryCacheSize(): number {
  return bnbGeometryPayloadCache.size
}
